import type { BackendModule, ReadCallback, Services } from 'i18next';
import { apiClient } from '../api/apiClient';
import {
  getCachedTranslations,
  isCacheStale,
  setCachedTranslations,
  invalidateCachedTranslations,
} from './translationCache';
interface AbpLanguageInfo {
  cultureName: string;
  uiCultureName: string;
  displayName: string;
  twoLetterISOLanguageName?: string;
  flagIcon?: string | null;
  isEnabled?: boolean;
}

interface AbpApplicationConfig {
  localization: {
    values: Record<string, Record<string, string>>;
    languages: AbpLanguageInfo[];
  };
}

let cachedLanguages: AbpLanguageInfo[] | null = null;
let languagesFetched = false;

export function getAbpLanguages() {
  return cachedLanguages;
}

async function fetchLanguagesIfNeeded(): Promise<void> {
  if (languagesFetched) return;
  try {
    const response = await apiClient.get<AbpApplicationConfig>(
      '/api/abp/application-configuration'
    );
    const langs = response.data.localization?.languages;
    if (langs && langs.length > 0) {
      cachedLanguages = langs;
      languagesFetched = true;
    }
  } catch {
    // Will retry next time
  }
}

async function fetchTranslationsFromPublicApi(
  culture: string
): Promise<Record<string, string>> {
  const response = await apiClient.get<Record<string, string>>(
    `/api/app/translations/${encodeURIComponent(culture)}`
  );
  return response.data;
}

const AbpBackend: BackendModule = {
  type: 'backend',

  init(_services: Services, _backendOptions: object) {},

  read(language: string, _namespace: string, callback: ReadCallback) {
    void (async () => {
      try {
        const cached = await getCachedTranslations(language);
        if (cached && !isCacheStale(cached.timestamp)) {
          if (!languagesFetched) fetchLanguagesIfNeeded();
          callback(null, cached.translations);
          return;
        }

        if (!languagesFetched) fetchLanguagesIfNeeded();
        const translations = await fetchTranslationsFromPublicApi(language);

        await setCachedTranslations(language, translations);
        callback(null, translations);
      } catch (err) {
        const cached = await getCachedTranslations(language);
        if (cached) {
          console.warn('[i18n] API failed, using stale cache for', language);
          callback(null, cached.translations);
          return;
        }
        callback(err as Error, false);
      }
    })();
  },
};

export async function invalidateAndReload(language: string): Promise<void> {
  await invalidateCachedTranslations(language);
  const i18n = (await import('./i18n')).default;
  await i18n.reloadResources(language);
}

export default AbpBackend;
